import { API_URL } from './apiUrl';

/**
 * Get the full image URL from the image path.
 * @param imagePath - The image path (e.g., "/images/filename.jpg")
 * @returns The full image URL (e.g., "http://localhost:8000/images/filename.jpg") or empty string
 */
export const getImageUrl = (imagePath: string | null | undefined): string => {
  if (!imagePath) return '';

  // If already a full URL, return as is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }

  // Prepend API URL
  return `${API_URL}${imagePath}`;
};

/**
 * Compress an image file to reduce its size.
 * @param file - The original image file
 * @param maxSizeKB - Maximum target size in KB (default: 300KB)
 * @param quality - Initial JPEG quality (default: 0.85)
 * @returns A compressed File object
 */
export const compressImage = async (
  file: File,
  maxSizeKB: number = 300,
  quality: number = 0.85
): Promise<File> => {
  // If file is already small enough, return as is
  const currentSizeKB = file.size / 1024;
  if (currentSizeKB <= maxSizeKB) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Resize if image is too large (max 1920px)
        const MAX_DIMENSION = 1920;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const ratio = MAX_DIMENSION / Math.max(width, height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Compress with decreasing quality until target size is reached
        let currentQuality = quality;
        let dataUrl = canvas.toDataURL('image/jpeg', currentQuality);
        
        // Reduce quality iteratively if still too large
        while (dataUrl.length / 1024 > maxSizeKB && currentQuality > 0.25) {
          currentQuality -= 0.15;
          if (currentQuality < 0.25) currentQuality = 0.25;
          dataUrl = canvas.toDataURL('image/jpeg', currentQuality);
        }
        
        // Convert dataURL to Blob
        fetch(dataUrl)
          .then(res => res.blob())
          .then(blob => {
            // Create new File with compressed blob
            const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
              type: 'image/jpeg',
            });
            resolve(compressedFile);
          })
          .catch(reject);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};
